import { animate, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { ProductsBespokeService } from 'src/app/services/products-bespoke.service';
import { environment } from '../../../../environments/environment';
import { ActionService } from '../../../services/action.service';
import { AlertService } from '../../../services/alert.service';
import { BasketService } from '../../../services/basket.service';
import { BreadcrumbsService } from '../../../services/breadcrumbs.service';
import { OverlayService } from '../../../services/overlay.service';
import { ProductService } from '../../../services/product.service';
import { Store } from '../../../store';
import { FacebookPixelService } from '../../tracking/services/facebook-pixel.service';
import { GoogleAnalyticsService } from '../../tracking/services/google-analytics.service';

@Component({
    selector: 'app-bespoke-builder-form',
    templateUrl: './bespoke-builder-form.component.html',
    styleUrls: ['./bespoke-builder-form.component.scss'],
    animations: [
        trigger('fadeIn', [
            transition(':enter', [style({ opacity: 0 }), animate('300ms ease-in', style({ opacity: 1 }))]),
        ]),
    ],
})
export class BespokeBuilderFormComponent implements OnInit {
    @Input() source = 'onPage';
    @Input() productToView;

    deviceType$ = this.store.select<string>('deviceType');
    overlays$ = this.store.select<any>('overlays');
    addingItem = false;
    uploadConfig: any;
    env = environment;
    resizeWidth: 500;
    domainId = '';
    productGroup: boolean = false;
    confirmMsg: boolean = false;

    confirmationData = {
        open: false,
        title: '',
        detail: '',
        data: undefined,
    };

    validHeight = true;
    validQuantity = true;
    pricingLog = [];
    scrollPoint = 0;
    configuration;
    costsSQM;
    loadingConfiguration = false;
    restrictions;
    allowRandom = true;
    showListTotals = true;
    showAddToListBanner = false;
    listHasItems = false;

    currentItems = [{ name: 'Your list', edited: false, expanded: true, items: [] }];

    viewCurrentItems = false;
    product;
    step = 'product-type';
    subStep = 'main';

    baseProduct = {
        supplierId: '',
        weightPerSellUnit: 0,
        weightTotal: 0,
        pricePerSellUnit: 0,
        length: undefined,
        totalLength: 0,
        useTotalLength: 0,
        width: undefined,
        height: 0,
        lengthRandom: false,
        widthRandom: false,
        cornersLR: 0,
        cornersRR: 0,
        ends: 0,
        weatheringLeft: 0,
        weatheringRight: 0,
        quantity: 1,
        quantityMetres: 1,
        leadtime: 15,
        estDeliveryDate: new Date(),
        unitCost: 0,
        totalCost: 0,
        unitCostBreakdown: this.productsBespokeService.resetUnitCostBreakdown(),
        quantityIsPieces: false,
        sellUnit: '',
        purchaseUnit: '',
        type: undefined,
        range: undefined,
        finish: undefined,
        weathering: undefined,
        additionalRequirements: undefined,
        riserRequired: false,
        finishTop: undefined,
        finishEdge: undefined,
        finishBottom: undefined,
        subTypes: [],
        notes: '',
        files: [],
    };

    productTypes = [];
    productRanges = [];
    productFinishes = [];
    subTypes = [];
    finishesEdge = [];
    finishesTop = [];
    finishesBottom = [];
    finishesAdditional = [];

    additionalRequirements = [];
    weathering = [];
    productGroupRanges = [];

    heightValidation = {
        setBy: 'size', // alternative is weathering
        valid: true,
        min: 0,
        max: 0,
    };

    pages = ['type'];
    currentPage = 'type';
    bespokeBuilderDesktop = false;

    bespokePricingLog$ = this.store.select<any[]>('bespokePricingLog');
    // @HostListener('window:scroll', ['$event'])
    // doSomething(event) {
    //     this.scrollPoint = window.pageYOffset;
    // }

    bespokeForm: FormGroup;
    formSubmitted = false;
    user;
    isTradeCustomer = false;
    registerWithYs = false;
    productGroupData: any;
    productGroupJsonData: any;
    productTypeValue: any;
    productOptionValue;
    any;

    constructor(
        private productService: ProductService,
        private overlayService: OverlayService,
        private productsBespokeService: ProductsBespokeService,
        private router: Router,
        private route: ActivatedRoute,
        private store: Store,
        private basketService: BasketService,
        private breadcrumbsService: BreadcrumbsService,
        private formBuilder: FormBuilder,
        private alertService: AlertService,
        private gaService: GoogleAnalyticsService,
        private fbqService: FacebookPixelService,
        private actionService: ActionService
    ) {
        const user = this.store.selectForLocal('user');
        if (user) {
            this.bespokeForm = this.formBuilder.group({
                type: [undefined, [Validators.required]],
                finish: [undefined, [Validators.required]],
                range: [undefined, [Validators.required]],
                weathering: ['', []],
            });
        } else {
            this.bespokeForm = this.formBuilder.group({
                type: [undefined, [Validators.required]],
                finish: [undefined, [Validators.required]],
                range: [undefined, [Validators.required]],
                email: ['', [Validators.required, Validators.email]],
                name: ['', [Validators.required]],
                telephone: ['', [Validators.required]],
                company: [''],
                weathering: ['', []],
            });
        }
    }

    ngOnInit(): void {
        if (this.source === 'onPage') {
            this.breadcrumbsService.changeBreadcrumb([{ text: 'Bespoke' }]);
        }

        this.product = JSON.parse(JSON.stringify(this.baseProduct));
        this.getBespokeProductTypes();
        this.getUserDetails();
    }
    getProductGroupData() {
        this.productsBespokeService.getProductGroups().subscribe((data) => {
            this.productGroupJsonData = data.data;
            this.productGroupData = data.data.reduce((acc, subGroup) => [].concat(acc, this.diggDeeper(subGroup)), []);
        });
    }

    private diggDeeper(subGroup) {
        return subGroup.subGroups.reduce(
            (acc, subSubGroup) => [].concat(acc, this.diggDeeper(subSubGroup)),
            [subGroup]
        );
    }

    getUserDetails() {
        this.user = this.store.selectForLocal('user');
        if (this.user) {
            this.bespokeForm.patchValue({
                name: `${this.user.contact.firstname} ${this.user.contact.surname}`,
                email: this.user.username,
                telephone: this.user.contact.mobile,
            });
        }
        this.initUpload();
    }

    get field() {
        return this.bespokeForm.controls;
    }

    initUpload() {
        this.uploadConfig = {
            url: this.env.imageUploadPath + 'bespoke-form/upload',
            acceptedFiles: 'image/jpeg,image/jpg,image/png,image/gif,application/pdf',
            parallelUploads: 1,
            resizeWidth: this.resizeWidth,
            maxFiles: 5,
            maxFilesize: 2048,
            params: {
                type: 'bespokeForm',
            },
        };
    }

    onSubmit() {
        this.confirmMsg = true;
        if (this.confirmMsg == true) {
            this.formSubmitted = true;

            if (this.bespokeForm.invalid && !this.validHeight && !this.validQuantity) {
                this.bespokeForm.markAllAsTouched();
                return;
            }

            this.product.type = this.bespokeForm.controls.type.value;
            this.product.finish = this.bespokeForm.controls.finish.value;
            this.product.range = this.bespokeForm.controls.range.value;

            const contact = {
                userId: '',
                name: null,
                email: null,
                telephone: null,
                mobDialCode: null,
                isTradeCustomer: false,
                companyName: undefined,
            };

            if (this.user) {
                contact.userId = this.user.id;
                contact.name = this.user.contact.firstname + ' ' + this.user.contact.surname;
                contact.email = this.user.contact.email;
                contact.telephone = this.user.contact.mobile ? this.user.contact.mobile : this.user.contact.landline;
                if (this.user.accountType === 'trade') {
                    contact.isTradeCustomer = true;
                }
            } else {
                contact.name = this.bespokeForm.controls.name.value;
                contact.email = this.bespokeForm.controls.email.value;

                contact.telephone = this.bespokeForm.controls.telephone.value.e164Number;
                contact.mobDialCode = this.bespokeForm.controls.telephone.value.dialCode;
                if (this.isTradeCustomer) {
                    contact.isTradeCustomer = this.isTradeCustomer;
                    contact.companyName = this.bespokeForm.controls.company.value;
                }
            }

            this.productsBespokeService
                .sendBespokeForm(this.product, contact, this.registerWithYs)
                .pipe(
                    switchMap(({ data }) => {
                        const { createdByUserId, bespokeFormId } = data;
                        const actionPayload = {
                            actionType: 4,
                            description: ' ',
                            title: 'Bespoke form submission',
                            source: 'bespoke_form',
                            status: 1,
                            createdBy: createdByUserId,
                        };
                        return this.actionService.createActionFromBespokeRequest({
                            action: actionPayload,
                            bespokeFormId,
                        });
                    })
                )
                .subscribe(
                    () => {
                        this.bespokeForm.reset();
                        this.formSubmitted = false;
                        this.gaService.event('submit_bespoke_quote_form', 'Ecommerce', 'Submit bespoke quote form');
                        this.fbqService.event('submit_bespoke_quote_form');
                        this.alertService.notification(['Thank you for your bespoke request.'], 4000);
                    },
                    (error) => {
                        this.alertService.error(['Sorry - there was a problem']);
                    }
                );
            this.close();
        }
    }

    typeSelected(e) {
        this.productTypeValue = e.id;
        this.setProductValues('type', e);
    }

    finishSelected(e) {
        this.productOptionValue = e.id;
        this.setProductValues('finish', e);
    }

    rangeSelected(e) {
        this.setProductValues('range', e);
    }

    loadConfiguration() {
        //this.overlayService.toggle('processing');
        this.loadingConfiguration = true;
        this.product.subTypes = [];
        this.productsBespokeService
            .loadBespokeConfiguration(this.product.type.id, this.product.supplierId, this.product.range.id)
            .subscribe((data) => {
                this.configuration = data.data;

                for (let i = 0; i < this.configuration.length; i++) {
                    if (this.product.finish.id === this.configuration[i].id) {
                        this.allowRandom = this.configuration[i].allowRandom;

                        this.restrictions = this.configuration[i].restrictions;
                        this.product.leadtimes = this.configuration[i].leadtimes;
                        this.product.purchaseUnit = this.configuration[i].costType;
                        this.product.sellUnit = this.configuration[i].sellUnit;
                        this.product.sellUnits = this.configuration[i].sellUnits;

                        this.subTypes = [];
                        for (let st = 0; st < this.configuration[i].subTypes.length; st++) {
                            if (
                                this.configuration[i].subTypes[st].finishesEdge.length ||
                                this.configuration[i].subTypes[st].finishesTop.length ||
                                this.configuration[i].subTypes[st].finishesBottom.length
                            ) {
                                // TODO i think this should go on the texture so if sawn and riven don't look right check here.
                                this.subTypes.push(this.configuration[i].subTypes[st]);
                                // end of

                                this.configuration[i].subTypes[st].finishTop = {
                                    front: '',
                                    back: '',
                                    left: '',
                                    right: '',
                                };
                                this.configuration[i].subTypes[st].finishEdge = {
                                    front: '',
                                    back: '',
                                    left: '',
                                    right: '',
                                };
                                this.configuration[i].subTypes[st].finishBottom = {
                                    front: '',
                                    back: '',
                                    left: '',
                                    right: '',
                                };
                            }
                        }
                        if (this.subTypes.length === 1) {
                            this.toggleSubType(this.subTypes[0]);
                        }
                    }
                }
                this.loadingConfiguration = false;
                // this.overlayService.toggle('processing');
            });
    }

    toggleMobileBespokeBuilder() {
        this.overlayService.toggle('bespokeBuilderModal');
    }

    toggleDesktopBespokeBuilder() {
        this.bespokeBuilderDesktop = !this.bespokeBuilderDesktop;
    }

    subTypeSelected(id) {
        for (let i = 0; i < this.product.subTypes.length; i++) {
            if (this.product.subTypes[i].id === id) {
                return true;
            }
        }
        return false;
    }

    toggleSubType(subType) {
        if (this.subTypeSelected(subType.id)) {
            for (let i = 0; i < this.product.subTypes.length; i++) {
                if (this.product.subTypes[i].id === subType.id) {
                    this.product.subTypes.splice(i, 1);
                }
            }
        } else {
            this.product.subTypes.push(subType);
        }
    }

    removeProductValues(type) {
        this.product[type] = undefined;
    }

    resetDimenions() {
        this.product.width = undefined;
        this.product.height = undefined;
        this.product.length = undefined;
        this.product.lengthRandom = false;
        this.product.riser = undefined;
    }

    setProductValues(type, value) {
        this.product[type] = value;
        if (type === 'type') {
            // this.getProductFinishes();
            this.getProductFinishesByProductType();
            this.bespokeForm.patchValue({
                finish: undefined,
                range: undefined,
                width: 0,
                length: 0,
                height: 0,
                quantity: 0,
            });

            this.product.finish = undefined;
            this.product.range = undefined;
            this.product.sellUnit = value.sellUnits[0].sellUnit;
            this.product.subTypes = [];
            this.resetDimenions();
        }
        if (type === 'finish') {
            this.getRangesByProductTypeOption(value.id);
            this.product.range = undefined;
            this.product.height = 40;

            // this.product.supplierId = value.supplier;
            this.getFinishesTop();
            this.getFinishesBottom();
            this.getAdditionalRequirements();
            this.getWeathering();
            this.getProductGroupData();
            // this.loadConfiguration();
            this.product.subTypes = [];
            // this.costsSQM = value.costsSQM;
        }
        if (type === 'range') {
            this.product.supplierId = value.supplier;
            this.getFinishesTop();
            this.getFinishesBottom();
            this.getAdditionalRequirements();
            this.getWeathering();
            this.loadConfiguration();
            this.product.subTypes = [];
            this.costsSQM = value.costsSQM;
        }

        this.resetProduct();
    }

    resetProduct() {
        this.product.weightPerSellUnit = 0;
        this.product.weightTotal = 0;
        this.product.pricePerSellUnit = 0;
        this.product.length = undefined;
        this.product.totalLength = 0;
        this.product.useTotalLength = 0;
        this.product.width = undefined;
        this.product.height = 40;
        this.product.lengthRandom = false;
        this.product.widthRandom = false;
        this.product.cornersLR = 0;
        this.product.cornersRR = 0;
        this.product.ends = 0;
        this.product.weatheringLeft = 0;
        this.product.weatheringRight = 0;
        this.product.quantity = 0;
        this.product.quantityMetres = 0;
        this.product.leadtime = 15;
        this.product.estDeliveryDate = new Date();
        this.product.unitCost = 0;
        this.product.totalCost = 0;
        this.product.unitCostBreakdown = this.productsBespokeService.resetUnitCostBreakdown();
        this.product.quantityIsPieces = false;
        this.product.sellUnit = '';
        this.product.purchaseUnit = '';
        this.product.weathering = undefined;
        this.product.additionalRequirements = undefined;
        this.product.riserRequired = false;
        this.product.riser = undefined;
        this.product.finishTop = {
            front: '',
            back: '',
            left: '',
            right: '',
        };
        this.product.finishEdge = {
            front: '',
            back: '',
            left: '',
            right: '',
        };
        this.product.finishBottom = {
            front: '',
            back: '',
            left: '',
            right: '',
        };
        this.product.subTypes = [];
    }

    loadFromURL() {
        let typeSelected;

        if (this.productToView) {
            this.productTypes.forEach((type) => {
                if (type.id === this.productToView.type.id) {
                    this.bespokeForm.patchValue({ type: type });
                    typeSelected = type;
                    this.typeSelected(type);
                    this.getProductFinishesByProductType();
                    this.productsBespokeService.getProductVariableOptionsByGroupBespoke(12).subscribe((data) => {
                        this.productFinishes = data;
                        this.productFinishes.forEach((finish) => {
                            if (finish.id === +this.productToView.finish) {
                                this.bespokeForm.patchValue({ finish });
                                this.finishSelected(finish);

                                this.productsBespokeService
                                    .getRangesByProductTypeOption(typeSelected.id, finish.id)
                                    .subscribe((data) => {
                                        this.productRanges = data.data;
                                        this.productRanges.forEach((r) => {
                                            if (r.id === +this.productToView.rangeId) {
                                                this.bespokeForm.patchValue({ range: r });
                                                this.rangeSelected(r);
                                            }
                                        });
                                    });
                            }
                        });
                    });
                }
            });
        }
    }

    getBespokeProductTypes() {
        this.productsBespokeService.getBespokeProductTypes().subscribe((data) => {
            this.productTypes = data;
            this.loadFromURL();
        });
    }

    getRangesByProductTypeOption(optionId) {
        this.productsBespokeService.getRangesByProductTypeOption(this.product.type.id, optionId).subscribe((data) => {
            this.productRanges = data;
        });
    }

    getProductFinishes() {
        this.productsBespokeService.getProductVariableOptionsByGroupBespoke(12).subscribe((data) => {
            this.productFinishes = data;
        });
    }

    getProductFinishesByProductType() {
        this.productsBespokeService.getProductFinishesByProductType(this.product.type.id).subscribe((data) => {
            this.productFinishes = data.data;
        });
    }

    getFinishesTop() {
        this.productsBespokeService.getProductVariableOptionsByGroupBespoke(9).subscribe((data) => {
            this.finishesTop = data;
        });
    }

    getFinishesBottom() {
        this.productsBespokeService.getProductVariableOptionsByGroupBespoke(10).subscribe((data) => {
            this.finishesBottom = data;
        });
    }

    getAdditionalRequirements() {
        this.productsBespokeService.getProductVariableOptionsByGroupBespoke(8).subscribe((data) => {
            this.additionalRequirements = data;
        });
    }

    getWeathering() {
        this.productsBespokeService.getProductVariableOptionsByGroupBespoke(11).subscribe((data) => {
            this.weathering = data;
        });
    }

    toggleExpanded(list) {
        if (!list.expanded) {
            list.expanded = true;
        } else {
            list.expanded = false;
        }
    }

    setValidHeight(e) {
        this.validHeight = e;
    }

    setValidQuantity(e) {
        this.validQuantity = e;
    }

    close() {
        this.overlayService.closeAll();
    }

    onUploadError(e) { }

    onUploadSuccess(response) {
        if (response.data) {
            this.product.files.push(response.data);
        }
    }

    uploadprogress(e) { }

    queueComplete(e) {
        // this.initUpload();
    }
    onChangeTrade(e) {
        const companyControl: AbstractControl = this.bespokeForm.controls["company"];
        if (e.target.checked) {
            this.isTradeCustomer = true;
            companyControl.setValidators([
                Validators.required
            ]);
        } else {
            this.isTradeCustomer = false;
            companyControl.clearValidators();
            companyControl.setErrors(null);
            companyControl.updateValueAndValidity();

        }
    }

    productGroupSelected(e) {
        if (e.level == 0) {
            var productGroupParentid = e.id;
        } else {
            let getProductGroup;
            for (const productGroup of this.productGroupJsonData) {
                getProductGroup = productGroup.subGroups.find((item) => item.id == e.id);
                if (getProductGroup) {
                    var productGroupParentid = productGroup.id;
                    break;
                }
            }
        }
        this.bespokeForm.controls['range'].reset();
        this.productsBespokeService
            .getBespokeProductTypesUsingProductGroup(
                productGroupParentid,
                this.productTypeValue,
                this.productOptionValue
            )
            .subscribe((data) => {
                this.productGroupRanges = data.data;
            });
    }
}
